/**
 * @author      Abdul Pathan
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState, useMemo } from 'react';
import WhatsAppChat from './WhatsAppChat';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NameInitialsAvatar } from 'react-name-initials-avatar';
import jwt_decode from 'jwt-decode';
import DataApi from '../../../api/DataApi';
import './WhatsAppMessenger.css';

const WhatsAppMessenger = ({ socket, selectedWhatsAppSetting }) => {
  const dataApi = useMemo(() => new DataApi('watconnect_history'), []);

  const [body, setBody] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('recentlymessage');
  const [userInfo] = useState(
    jwt_decode(localStorage.getItem('watconnect_token'))
  );
  const [bgColors] = useState([
    '#d3761f',
    '#00ad5b',
    '#debf31',
    '#239dd1',
    '#b67eb1',
    '#de242f',
  ]);
  const [brokenImages, setBrokenImages] = useState([]);
  const [unreadMsgCount, setUnreadMsgCount] = useState([]);
  const profileImage = '/public/' + userInfo.tenantcode + '/users';

  let colorIndex = 0;

  const fetchData = async () => {
    let result = await dataApi.fetchAll(
      `filter=${activeFilter}&search=${encodeURIComponent(searchText || '')}&businessNumber=${encodeURIComponent(selectedWhatsAppSetting || '')}`
    );

    if (result?.data?.success) {
      const newRecords = result?.data?.records || [];
      setBody(newRecords);
    } else {
      setBody([]);
    }
  };

  // SOCKET listener
  useEffect(() => {
    if (!socket) return;
    const handleMessage = () => fetchData();
    socket.on('receivedwhatsappmessage', handleMessage);
    return () => socket.off('receivedwhatsappmessage', handleMessage);
  }, [socket]);

  // FETCH logic
  useEffect(() => {
    fetchData();
    getUnreadMsgCounts(selectedWhatsAppSetting);
  }, [searchText, selectedWhatsAppSetting, activeFilter]);

  const getUnreadMsgCounts = async () => {
    try {
      const dataApi = new DataApi('watconnect_history/unread_count');

      let result = await dataApi.fetchAll(
        `whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`
      );

      if (result && result.data && result.data.success && result.data.records) {
        const messages = result.data.records;
        setUnreadMsgCount(messages);
      } else {
        console.log('No unread messages found in WhatsAppMessenger');
        setUnreadMsgCount([]);
      }
    } catch (error) {
      console.error(
        'Error fetching unread messages in WhatsAppMessenger:',
        error
      );
      setUnreadMsgCount([]);
    }
  };

  const markMessagesAsRead = async whatsappNumber => {
    try {
      const dataApi = new DataApi('watconnect_history/mark_as_read');
      const result = await dataApi.post(
        `?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`,
        { whatsapp_number: whatsappNumber }
      );

      console.log('Mark as read response in WhatsAppMessenger:', result);

      if (result && result.data && result.data.success) {
        console.log(
          'Messages marked as read successfully in WhatsAppMessenger'
        );
        // Refresh unread counts after marking as read
        getUnreadMsgCounts();
        return true;
      } else {
        console.log('Failed to mark messages as read in WhatsAppMessenger');
        return false;
      }
    } catch (error) {
      console.error(
        'Error marking messages as read in WhatsAppMessenger:',
        error
      );
      return false;
    }
  };

  const handleUserClick = (item, unreadCount) => {
    setShowChat(true);
    setSelectedUser(item);
    if (item.whatsapp_number && unreadCount > 0) {
      const fullNumber = item.whatsapp_number?.startsWith('+')
        ? item.whatsapp_number
        : `${item.whatsapp_number}`;
      markMessagesAsRead(fullNumber);
    }
  };

  const getAvatarColor = () => {
    const color = bgColors[colorIndex % bgColors.length];
    colorIndex++;
    return color;
  };

  const getUnreadCount = item => {
    const fullNumber = item.whatsapp_number?.startsWith('+')
      ? item.whatsapp_number
      : `${item.whatsapp_number}`;
    const unread = unreadMsgCount.find(
      unread => unread.whatsapp_number === fullNumber
    );
    return unread?.unread_msg_count || 0;
  };

  return (
    <>
      <div className='wa-container'>
        <div className='wa-wrapper'>
          {/* LEFT SIDEBAR */}
          <div className='wa-sidebar'>
            {/* Header */}
            <div className='wa-sidebar-header'>
              <NameInitialsAvatar
                size='40px'
                textSize='16px'
                bgColor='#6B7C85'
                borderWidth='0px'
                textColor='#fff'
                name={userInfo?.username || userInfo?.name || 'User'}
              />
            </div>

            {/* Search */}
            <div className='wa-search'>
              <i className='fa-solid fa-magnifying-glass'></i>
              <input
                type='text'
                placeholder='Search or start new chat'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>

            {/* Tabs */}
            <div className='wa-tabs'>
              {[
                { label: 'New Messages', value: 'recentlymessage' },
                { label: 'Unread', value: 'unread' },
                { label: 'Leads', value: 'lead' },
                { label: 'Contacts', value: 'contact' },
              ].map(filter => (
                <button
                  key={filter.value}
                  className={`wa-tab ${
                    activeFilter === filter.value ? 'active' : ''
                  }`}
                  onClick={() => setActiveFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Contacts */}
            <div className='wa-contact-list'>
              {body.length > 0 ? (
                body.map(item => {
                  const unreadCount = getUnreadCount(item);
                  const isActive = selectedUser?.id === item.id;

                  return (
                    <div
                      key={item.id}
                      className={`wa-contact ${isActive ? 'active' : ''}`}
                      onClick={() => handleUserClick(item, unreadCount)}
                    >
                      <div className='wa-avatar'>
                        <NameInitialsAvatar
                          size='40px'
                          textSize='16px'
                          bgColor={getAvatarColor()}
                          borderWidth='0px'
                          textColor='#fff'
                          name={
                            item?.name ||
                            item?.contactname ||
                            item?.whatsapp_number ||
                            '?'
                          }
                        />
                      </div>

                      <div className='wa-contact-info'>
                        <div className='wa-contact-top'>
                          <span className='wa-contact-name'>
                            {item?.name || item?.contactname}
                          </span>
                        </div>
                        <div className='wa-contact-bottom'>
                          <span className='wa-contact-number'>
                            {item?.whatsapp_number}
                          </span>
                          {unreadCount > 0 && (
                            <span className='wa-badge float-end'>
                              {unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className='wa-empty'>No conversations found</div>
              )}
            </div>
          </div>

          {/* RIGHT CHAT AREA */}
          <div className='wa-chat'>
            {showChat && selectedUser ? (
              <WhatsAppChat
                isOpen={showChat}
                onToggle={() => setShowChat(false)}
                onClose={() => setShowChat(false)}
                recipientName={selectedUser?.name}
                recipientPhone={selectedUser?.whatsapp_number}
                socket={socket}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
              />
            ) : (
              <div className='wa-chat-empty'>
                <div className='wa-logo'>
                  <i className='fa-brands fa-whatsapp'></i>
                </div>
                <h2>WhatsApp Web</h2>
                <p>
                  Send and receive messages without keeping your phone online.
                  <br />
                  Use WhatsApp on up to 4 linked devices and 1 phone at the same
                  time.
                </p>
                <div className='wa-encryption'>
                  <i className='fa-solid fa-lock'></i> End-to-end encrypted
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default WhatsAppMessenger;
